/**
 * AliasRow — concrete variant for Figma-style template instances.
 *
 * Detail: `sheets/.docs/content/rows-aliases.md`.
 */

import { Json, jsonOf, type JsonObject } from "../../core/json.ts";
import type { AliasDescriptor } from "../descriptor.ts";
import type { RowWriteError } from "../../core/errors.ts";
import type { Option } from "../../core/option.ts";
import type { AliasInstance } from "../../stores/alias-instance-store.ts";
import { RowMetadataStore } from "../../stores/row-metadata-store.ts";
import type { Actor } from "../../tx/invalidation.ts";
import { RowWrites } from "../../ledger/row-writes.ts";
import type { RowLocalId } from "../../ids/row-local-id.ts";
import type { ModelSheet } from "../../sheet/rows-sheet.ts";
import { sheetInternals, sheetOf, sheetStores } from "../../sheet/sheet-stores.ts";
import { ModelRow } from "../model-row.ts";
import type { PropertyListProjection } from "../../render/tree-projection.ts";
import { Internals } from "../../core/internals.ts";

/** `AliasRow`'s friend record (`Internals`): its constructor registers each instance. */
type AliasRowInternals = {
  readonly writer: (row: AliasRow) => AliasRow.Writer;
};
const aliasRowInternals = new Internals<AliasRow, AliasRowInternals>("AliasRow");

/**
 * **The writers of an alias row** — its prop write, fork and reset
 * (`AliasRow.Writer`). Not on the row: an `AliasRow` a read hands out
 * (`handle.model`, `row.variant`, `RowKind.of`) carries no writer. The
 * package's own envelopes write through it; the SDK layer reaches it through
 * `@tento-lona/sheets/internal-sdk` (an app writes through the SDK row's
 * alias verbs, `row.alias()?.setProp(name, value, actor)` (`SheetRow.Joined.Handle`), and the handle's `detachAlias(id)`), this
 * repository's tests through `@tento-lona/sheets/internal-testing`.
 */
export function aliasWriterOf(row: AliasRow): AliasRow.Writer {
  return aliasRowInternals.of(row).writer(row);
}

/**
 * A row that expands an alias template into child rows (a calendar list, a
 * weather row, a data chart).
 */
export class AliasRow extends ModelRow {
  declare readonly descriptor: AliasDescriptor;

  static readonly #internals: AliasRowInternals = {
    writer: (row) => row.#writerFace(),
  };

  /** This row's writer face (`aliasWriterOf`), built on first use. */
  #writer: Option<AliasRow.Writer> = null;

  #writerFace(): AliasRow.Writer {
    if (this.#writer != null) return this.#writer;
    const writer: AliasRow.Writer = Object.freeze({
      row: this,
      setProp: (name: string, value: Json) => this.#setProp(name, value, RowMetadataStore.ACTOR, RowMetadataStore.ACTOR),
      setPropAs: (name: string, value: Json, actor: Actor, correction: Actor) =>
        this.#setProp(name, value, actor, correction),
      adoptServerProps: (props: Option<JsonObject>, actor: Actor) => this.#adoptServerProps(props, actor),
      detach: () => this.#detach(),
      reset: () => this.#reset(),
    });
    this.#writer = writer;
    return writer;
  }

  constructor(id: RowLocalId, descriptor: AliasDescriptor, sheet: ModelSheet) {
    super(id, descriptor, sheet);
    aliasRowInternals.register(this, AliasRow.#internals);
  }

  /** Current instance state (template, props, overrides, hydration). */
  instance(): AliasInstance {
    return sheetStores(sheetOf(this)).aliasInstance.get(this.id);
  }

  /**
   * Human-readable name for this alias, derived from its template
   * (e.g. `alias.calendar` → "Calendar"). Used as the display label
   * for alias rows without an explicit `label` so they render a
   * proper name instead of the literal "alias". Subclasses may
   * override for dynamic naming.
   */
  get displayName(): string {
    return sheetOf(this).aliasDisplayName(this.descriptor.template);
  }

  /**
   * Attached aliases collapse to a single leaf showing their template
   * name. A forked (detached) alias is a plain editable group, so it
   * expands to reveal its now-real children.
   */
  override get defaultCollapsedInPropertyList(): boolean {
    return !this.instance().hydrated;
  }

  /**
   * In `default` mode an attached alias is a `"leaf"` — it subsumes its
   * template subtree behind the single alias row. A forked (detached)
   * alias is a real editable group, so it projects as a `"branch"` and
   * reveals its now-real children (matching `defaultCollapsedInPropertyList`).
   */
  override get defaultProjection(): PropertyListProjection.DefaultProjection {
    return this.instance().hydrated ? "branch" : "leaf";
  }

  /**
   * Update a named prop (`AliasRow.Writer.setProp`): a write of the row's
   * `attributes.props` bag entered in the client's row-field ledger
   * (`RowWrites`) — painted at once into every copy of the row (the
   * instance props, the metadata bag, the SDK's row), announced as `actor`
   * (`props`, `props.<name>`), then sent through the sheet's backend
   * (`Backend.updateRow`). The wire's row PATCH replaces `attributes` whole,
   * so the body is the row's whole projected bag when it is sent — every
   * write of the row still in flight in it.
   *
   * A refusal (or no answer) takes back this write's own prop — never a
   * newer write's — announced as `correction`, PATCHes the surviving bag
   * again when a later PATCH carried it, and rejects with `RowWriteError`
   * (step `"update"`).
   *
   * If the prop maps to a template binding, child attributes update
   * through the binding (downward flow runs at the next re-expansion;
   * see `expandAliasInstance`).
   */
  async #setProp(name: string, value: Json, actor: Actor, correction: Actor): Promise<void> {
    const internals = sheetInternals(sheetOf(this));
    const write = internals.rowWrites.begin(
      this.id,
      RowWrites.mergeAttributes("deep", { props: { [name]: jsonOf(value) ?? null } }, []),
      actor,
      { correction, quietAttributes: false, capture: null },
    );
    await internals.rowWrites.run(
      write,
      internals.rowTransport(this.id),
      `the alias prop ${name} of ${this.id.rawId} was not saved`,
    );
  }

  /**
   * Show `props` — the `attributes.props` bag the server holds, re-read
   * after a prop write that may have stored (`AliasRow.Writer.adoptServerProps`)
   * — as this row's props (an authoritative read of the bag through the
   * row-field ledger, announced as `actor`), when no attribute write of the
   * row is still in flight; answers whether it did. A write in flight's own
   * answer settles the bag (the wire's PATCH replaces it whole), so the bag
   * is not laid over it. `null` is an empty bag.
   */
  #adoptServerProps(props: Option<JsonObject>, actor: Actor): boolean {
    const internals = sheetInternals(sheetOf(this));
    if (internals.rowWrites.inFlight(this.id, "attributes")) return false;
    const current = internals.peekRowFields(this.id)?.attributes ?? null;
    const read = internals.rowWrites.beginRead(this.id);
    internals.rowWrites.endRead(read, { attributes: { ...(Json.object(current) ?? {}), props: props ?? {} } }, actor);
    return true;
  }

  /**
   * Detach from the master — fork into an editable group
   * (`AliasRow.Writer.detach`). Promotes the alias's synthetic children into
   * real, server-backed rows, flips the instance to `hydrated: true`, and
   * persists both. After this the children are independently
   * movable/editable and the template no longer drives expansion. See
   * `Sheet.detachAlias`.
   */
  async #detach(): Promise<ReadonlyMap<RowLocalId, RowLocalId>> {
    return await sheetOf(this).detachAlias(this.id);
  }

  /**
   * Reset to main component — discard the fork (`AliasRow.Writer.reset`).
   * Deletes the promoted children, clears overrides, flips back to
   * `hydrated: false`, and re-expands from the template. See
   * `Sheet.resetAlias`.
   */
  async #reset(): Promise<void> {
    await sheetInternals(sheetOf(this)).resetAlias(this.id);
  }

  override toDebug(): Record<string, unknown> {
    return {
      ...super.toDebug(),
      template: this.descriptor.template,
      instance: this.instance(),
    };
  }
}

/** Types that go with `AliasRow`: `Writer`. */
export namespace AliasRow {
  /**
   * An alias row's writers, reached through `aliasWriterOf(row)` only (see
   * there). `setProp` paints at once and, on a refusal, takes back its own
   * prop (the row-field ledger's rule) before rejecting with
   * `RowWriteError`; `detach` and `reset` are the sheet's fork and reset of
   * this alias.
   */
  export interface Writer {
    /** The row these writers write. */
    readonly row: AliasRow;
    /** Checked prop write: paint, persist, take this write back on a refusal (announced as `RowMetadataStore.ACTOR`). */
    setProp(name: string, value: Json): Promise<void>;
    /** `setProp` announced as `actor`, its take-back as `correction` (the SDK row's `alias().setProp`). */
    setPropAs(name: string, value: Json, actor: Actor, correction: Actor): Promise<void>;
    /** Show the server's `attributes.props` bag (re-read after a prop write
     *  that may have stored), announced as `actor`, unless an attribute write
     *  of the row is still in flight; answers whether it was shown. */
    adoptServerProps(props: Option<JsonObject>, actor: Actor): boolean;
    /** Fork into an editable group; resolves the old → new id of every promoted row. */
    detach(): Promise<ReadonlyMap<RowLocalId, RowLocalId>>;
    /** Discard the fork and re-expand from the template. */
    reset(): Promise<void>;
  }
}
